import { type RadioChangeEvent } from "antd/lib/radio/interface";

import {
  BuiRadio,
  BuiRadioGroup,
  type BuiRadioProps,
  type IBuiRadioGroupProps,
} from "@btnet/blueprint-components/BuiRadioGroup";

import {
  BTAnalyticsEvent,
  BTTrack,
  type IBTTrackingProps,
  trackBTEvent,
} from "../analytics/analytics";

export interface IBTRadioGroupProps extends IBuiRadioGroupProps {
  /**
   * A recognizable, stable identifier for analytics. Falls back to `data-testid`
   * when omitted. See `trackBTEvent`.
   */
  analyticId?: string;
}

export type IBTRadioProps = BuiRadioProps;

/**
 * BTRadioGroup wraps the Blueprint `BuiRadioGroup` primitive and re-adds the
 * value-change analytics tracking that the primitive intentionally omits.
 *
 * Use `BTRadio` for the individual options (or pass the `options` prop).
 * @example
 * <BTRadioGroup id="contractType" data-testid="contractType" defaultValue="1">
 *   <BTRadio value="1">Fixed price</BTRadio>
 *   <BTRadio value="2">Open book</BTRadio>
 * </BTRadioGroup>
 */
export const BTRadioGroup = BTTrack({ element: "BTRadioGroup" })(({
  onChange,
  tracking,
  trackingEvent,
  analyticId = "BTRadioGroup",
  "data-testid": testid,
  ...otherProps
}: IBTRadioGroupProps & IBTTrackingProps) => {
  const handleChange = (e: RadioChangeEvent) => {
    trackBTEvent(tracking, {
      element: "BTRadioGroup",
      event: trackingEvent ?? BTAnalyticsEvent.ValueChange,
      analyticId,
      testid,
      value: e.target.value,
    });
    onChange?.(e);
  };

  return (
    <BuiRadioGroup
      {...otherProps}
      onChange={handleChange}
      {...(testid !== undefined ? { "data-testid": testid } : {})}
    />
  );
});

/**
 * BTRadio is an individual radio option used within `BTRadioGroup`. It is a thin
 * passthrough to the Blueprint `BuiRadio` primitive.
 */
export const BTRadio = (props: IBTRadioProps) => <BuiRadio {...props} />;
